import { useEffect } from 'react';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { useAuth } from '@/features/auth/AuthContext';
import { keyboardManager } from '@/shared/services/KeyboardEventManager';
import { log } from '@/shared/utils/logger';

/**
 * Keyboard shortcuts for undo/redo using centralized keyboard management
 * - Ctrl+Z / Cmd+Z: Undo
 * - Ctrl+Shift+Z / Cmd+Shift+Z: Redo
 * - Ctrl+Y / Cmd+Y: Alternative Redo (Windows style)
 */
export const useUndoShortcuts = () => {
  const { undo, redo, undoState } = useProjectData();
  const { hasPermission } = useAuth();

  useEffect(() => {
    // Check if user has permission for history operations
    if (!hasPermission('system', 'history')) {
      return;
    }

    // Register undo handler for Ctrl+Z (Windows/Linux)
    const unregisterUndoCtrl = keyboardManager.register({
      id: 'global-undo-ctrl',
      key: 'z',
      modifiers: { ctrl: true },
      priority: 50, // Medium priority - allows modals to override
      handler: () => {
        // Don't check undoState.canUndo - it might be stale due to React's async state updates
        // The undo() function checks the live authoritative operation history
        undo().catch(error => {
          // Only log if it's not a "nothing to undo" error
          if (!error?.message?.includes('Nothing to undo')) {
            log.error('useUndoShortcuts', 'Keyboard undo failed:', error);
          }
        });
      },
      preventDefault: true,
      stopPropagation: true,
      allowInInput: false,
      // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
      description: 'Undo last action (Ctrl+Z)'
    });

    // Register undo handler for Cmd+Z (Mac)
    const unregisterUndoCmd = keyboardManager.register({
      id: 'global-undo-cmd',
      key: 'z',
      modifiers: { cmd: true },
      priority: 50,
      handler: () => {
        // Don't check undoState.canUndo - it might be stale due to React's async state updates
        // The undo() function checks the live authoritative operation history
        undo().catch(error => {
          // Only log if it's not a "nothing to undo" error
          if (!error?.message?.includes('Nothing to undo')) {
            log.error('useUndoShortcuts', 'Keyboard undo failed:', error);
          }
        });
      },
      preventDefault: true,
      stopPropagation: true,
      allowInInput: false,
      // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
      description: 'Undo last action (Cmd+Z)'
    });

    // Register redo handler for Ctrl+Shift+Z (Windows/Linux)
    const unregisterRedoShiftCtrl = keyboardManager.register({
      id: 'global-redo-shift-ctrl',
      key: 'z',
      modifiers: { ctrl: true, shift: true },
      priority: 50,
      handler: () => {
        // Don't check undoState.canRedo - it might be stale due to React's async state updates
        redo().catch(error => {
          if (!error?.message?.includes('Nothing to redo')) {
            log.error('useUndoShortcuts', 'Keyboard redo failed:', error);
          }
        });
      },
      preventDefault: true,
      stopPropagation: true,
      allowInInput: false,
      // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
      description: 'Redo last undone action (Ctrl+Shift+Z)'
    });

    // Register redo handler for Cmd+Shift+Z (Mac)
    const unregisterRedoShiftCmd = keyboardManager.register({
      id: 'global-redo-shift-cmd',
      key: 'z',
      modifiers: { cmd: true, shift: true },
      priority: 50,
      handler: () => {
        // Don't check undoState.canRedo - it might be stale due to React's async state updates
        redo().catch(error => {
          if (!error?.message?.includes('Nothing to redo')) {
            log.error('useUndoShortcuts', 'Keyboard redo failed:', error);
          }
        });
      },
      preventDefault: true,
      stopPropagation: true,
      allowInInput: false,
      // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
      description: 'Redo last undone action (Cmd+Shift+Z)'
    });

    // Register alternative redo handler Ctrl+Y (Windows/Linux)
    const unregisterRedoYCtrl = keyboardManager.register({
      id: 'global-redo-y-ctrl',
      key: 'y',
      modifiers: { ctrl: true },
      priority: 50,
      handler: () => {
        // Don't check undoState.canRedo - it might be stale due to React's async state updates
        redo().catch(error => {
          if (!error?.message?.includes('Nothing to redo')) {
            log.error('useUndoShortcuts', 'Keyboard redo failed:', error);
          }
        });
      },
      preventDefault: true,
      stopPropagation: true,
      allowInInput: false,
      // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
      description: 'Redo last undone action (Ctrl+Y)'
    });

    // Register alternative redo handler Cmd+Y (Mac)
    const unregisterRedoYCmd = keyboardManager.register({
      id: 'global-redo-y-cmd',
      key: 'y',
      modifiers: { cmd: true },
      priority: 50,
      handler: () => {
        // Don't check undoState.canRedo - it might be stale due to React's async state updates
        redo().catch(error => {
          if (!error?.message?.includes('Nothing to redo')) {
            log.error('useUndoShortcuts', 'Keyboard redo failed:', error);
          }
        });
      },
      preventDefault: true,
      stopPropagation: true,
      allowInInput: false,
      // i18n-raw-ui-literal-exempt: Keyboard-manager registration metadata, not rendered product UI.
      description: 'Redo last undone action (Cmd+Y)'
    });

    // Cleanup
    return () => {
      unregisterUndoCtrl();
      unregisterUndoCmd();
      unregisterRedoShiftCtrl();
      unregisterRedoShiftCmd();
      unregisterRedoYCtrl();
      unregisterRedoYCmd();
    };
  }, [undo, redo, hasPermission]);

  // Return current state for components that might want to display it
  return {
    canUndo: undoState.canUndo,
    canRedo: undoState.canRedo,
    position: undoState.position,
    stackSize: undoState.stackSize
  };
};
